// src/api/axios.js
import axios from 'axios';
import { resolveLobbyApiBase } from '../config/serviceUrl';
import { ensureFreshAccessToken, getStoredAccessToken, isDefinitiveAuthFailure, refreshAccessToken } from './authSession';

const API_BASE = resolveLobbyApiBase();

const AUTH_SESSION_ERRORS = new Set([
  'SESSION_MISSING',
  'SESSION_EXPIRED',
  'SESSION_MISMATCH',
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_REVOKED',
  'NO_TOKEN',
]);

function clearAuthAndRedirectToLogin(reason = 'session') {
  // socket.js가 로드된 로비/게임 화면에서는 동기 CustomEvent로 먼저 모든 로그인 종속 소켓을 끊는다.
  try {
    window.dispatchEvent(new CustomEvent('npc:auth-force-clear', { detail: { reason } }));
  } catch (_) {}

  try {
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('jwt');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('user_code');
    localStorage.removeItem('userCode');
  } catch (_) {}

  try {
    const path = window.location?.pathname || '';
    if (path !== '/login') {
      window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
    }
  } catch (_) {}
}

function attachAuthInterceptors(instance) {
  instance.interceptors.request.use(
    async (config) => {
      let token = getStoredAccessToken();
      if (token && !config.__npcSkipAuthRefresh) {
        try {
          token = await ensureFreshAccessToken();
        } catch (refreshError) {
          // Redis/네트워크 일시 장애는 기존 토큰으로 요청을 계속 시도한다.
          // 확정적인 세션 무효는 응답 인터셉터가 최종 처리한다.
          token = getStoredAccessToken();
        }
      }
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = Number(error?.response?.status || 0);
      const code = String(error?.response?.data?.error || '').trim();
      const url = String(error?.config?.url || '');
      const isLoginRequest = /(^|\/)login(\?|$)/.test(url);
      const isRefreshRequest = /\/session\/refresh(\?|$)/.test(url);
      const original = error?.config || {};

      // Access Token만 만료된 경우 서버 로그인 세션이 살아있으면 새 토큰을 받아 1회 재시도한다.
      if (!isLoginRequest && !isRefreshRequest && status === 401 && code === 'TOKEN_EXPIRED' && !original.__npcAuthRetried) {
        try {
          const nextToken = await refreshAccessToken();
          original.__npcAuthRetried = true;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${nextToken}`;
          return instance.request(original);
        } catch (refreshError) {
          if (isDefinitiveAuthFailure(refreshError)) {
            clearAuthAndRedirectToLogin(String(refreshError?.code || 'session'));
          }
          return Promise.reject(error);
        }
      }

      // SESSION_STORE_UNAVAILABLE은 503 일시 장애이므로 절대 로컬 인증을 삭제하지 않는다.
      if (!isLoginRequest && status === 401 && AUTH_SESSION_ERRORS.has(code)) {
        clearAuthAndRedirectToLogin(code || 'session');
      }
      return Promise.reject(error);
    }
  );
}

const api = axios.create({ baseURL: API_BASE });
attachAuthInterceptors(api);

const userApi = axios.create({ baseURL: `${API_BASE}/user` });
attachAuthInterceptors(userApi);

export default api;
export { userApi, clearAuthAndRedirectToLogin };
